
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { CategoryDefinition } from "@/types/habit";
import { toast } from "@/hooks/use-toast";
import { CATEGORIES } from "@/constants/categories";

export const useSupabaseCategories = () => {
  const [categories, setCategories] = useState<CategoryDefinition[]>(CATEGORIES);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("categories")
          .select("*")
          .order("name");

        if (error) throw error;
        
        if (data && data.length > 0) {
          const formattedCategories = data.map(cat => ({
            name: cat.name,
            description: cat.description || `Activities related to ${cat.name}`,
            iconSrc: cat.icon_src
          }));
          setCategories([...CATEGORIES, ...formattedCategories]);
        } else {
          // If no custom categories yet, use the defaults
          setCategories(CATEGORIES);
        }
      } catch (error: any) {
        console.error("Error fetching categories:", error);
        toast({
          title: "Failed to load categories",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
    
    // Subscribe to real-time changes
    const categoryChanges = supabase
      .channel('public:categories')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'categories' }, 
        () => {
          fetchCategories();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(categoryChanges);
    };
  }, [user]);

  const handleRemoveCategory = async (categoryName: string) => {
    if (!user) return;
    
    // Don't allow removing default categories
    const isDefaultCategory = CATEGORIES.some(cat => cat.name === categoryName);
    if (isDefaultCategory) {
      toast({
        title: "Cannot delete default category",
        description: "Default categories cannot be removed",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const { error } = await supabase
        .from("categories")
        .delete()
        .eq("name", categoryName);

      if (error) throw error;
      
      // Update local state
      setCategories(prevCategories => 
        prevCategories.filter(cat => cat.name !== categoryName)
      );
      
      toast({
        title: "Category deleted",
        description: `Category "${categoryName}" has been removed`,
      });
    } catch (error: any) {
      console.error("Error removing category:", error);
      toast({
        title: "Failed to delete category",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleAddNewCategoryToSystem = async (category: string, iconSrc?: string) => {
    if (!user || !category.trim()) return;
    
    const normalizedCategory = category.trim();
    const categoryExists = categories.some(cat => cat.name === normalizedCategory);
    
    if (categoryExists) {
      toast({
        title: "Category already exists",
        description: `A category named "${normalizedCategory}" already exists`,
        variant: "destructive",
      });
      return;
    }
    
    try {
      const { error } = await supabase
        .from("categories")
        .insert({
          name: normalizedCategory,
          description: `Activities related to ${normalizedCategory}`,
          icon_src: iconSrc,
          user_id: user.id
        });

      if (error) throw error;
      
      const newCategory: CategoryDefinition = {
        name: normalizedCategory,
        description: `Activities related to ${normalizedCategory}`,
        iconSrc: iconSrc
      };
      
      setCategories(prevCategories => [...prevCategories, newCategory]);
      
      toast({
        title: "Category added",
        description: `New category "${normalizedCategory}" has been created`,
      });
    } catch (error: any) {
      console.error("Error adding category:", error);
      toast({
        title: "Failed to add category",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleUpdateCategory = async (categoryName: string, newName: string, newIconSrc?: string) => {
    if (!user || !newName.trim() || (categoryName === newName.trim() && newIconSrc === undefined)) return;
    
    // Don't allow updating default categories
    const isDefaultCategory = CATEGORIES.some(cat => cat.name === categoryName);
    if (isDefaultCategory) {
      toast({
        title: "Cannot update default category",
        description: "Default categories cannot be modified",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const updateData: Record<string, any> = { name: newName.trim() };
      if (newIconSrc !== undefined) {
        updateData.icon_src = newIconSrc;
      }
      
      const { error } = await supabase
        .from("categories")
        .update(updateData)
        .eq("name", categoryName);

      if (error) throw error;
      
      setCategories(prevCategories =>
        prevCategories.map(category => {
          if (category.name === categoryName) {
            return {
              ...category,
              name: newName.trim(),
              ...(newIconSrc !== undefined && { iconSrc: newIconSrc })
            };
          }
          return category;
        })
      );
      
      toast({
        title: "Category updated",
        description: `Category "${categoryName}" has been updated`,
      });
    } catch (error: any) {
      console.error("Error updating category:", error);
      toast({
        title: "Failed to update category",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getAllCategories = () => {
    return categories;
  };

  return {
    categories,
    loading,
    handleRemoveCategory,
    handleAddNewCategoryToSystem,
    handleUpdateCategory,
    getAllCategories,
  };
};
